import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import './EducationCurriculumOverviewForm.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function EducationCurriculumOverviewForm({ apiBase }) {
  const toastUtil = useToast();
  const ToastContainer = toastUtil.ToastContainer;
  const toast = toastUtil;

  const [formData, setFormData] = useState({
    title: '',
    summary: '',
    note: '',
    pillars: [],
    tracks: []
  });

  const [pendingFields, setPendingFields] = useState({});
  const [originalData, setOriginalData] = useState({...formData});
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [imageUploading, setImageUploading] = useState({});
  const [imageProgress, setImageProgress] = useState({});
  const [activeTab, setActiveTab] = useState('overview');
  const [editingTrackIndex, setEditingTrackIndex] = useState(null);
  const [modalTrackData, setModalTrackData] = useState(null);
  const [mediaMap, setMediaMap] = useState({});

  useEffect(() => {
    fetch(`${apiBase}/auth/me`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        console.log('[EducationCurriculumOverviewForm] Auth status:', data);
      })
      .catch(err => console.log('[EducationCurriculumOverviewForm] Auth check failed:', err.message));
    
    // Load mediaMap FIRST, then loadSection after mediaMap is ready
    const initializeForm = async () => {
      await loadMediaMap();
      await loadSection();
    };
    
    initializeForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      const response = await fetch(
        `${apiBase}/education/cms/media?_t=${Date.now()}`,
        { 
          credentials: 'include',
          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
        }
      );
      
      if (!response.ok) {
        console.warn('[EducationCurriculumOverviewForm] Media fetch failed:', response.status);
        return {}; // Return empty map on failure
      }
      
      const result = await response.json();
      
      if (result.ok && Array.isArray(result.data)) {
        const map = {};
        result.data.forEach((media) => {
          const mediaId = media.id || media.media_id;
          const mediaUrl = media.url || media.file_path;
          
          if (mediaId && mediaUrl) {
            map[mediaId] = mediaUrl;
            map[String(mediaId)] = mediaUrl;
          }
        });
        
        console.log('[EducationCurriculumOverviewForm] ✓ Media map loaded:', Object.keys(map).length, 'items');
        setMediaMap(map);
        return map;
      }
      return {};
    } catch (err) {
      console.error('[EducationCurriculumOverviewForm] Media fetch error:', err.message);
      return {};
    }
  };

  const loadSection = async () => {
    setLoading(true);
    try {
      console.log('[EducationCurriculumOverviewForm] Loading curriculum overview section...');
      
      let curriculumSection = null;
      
      // Try admin endpoint
      console.log('[EducationCurriculumOverviewForm] Trying authenticated endpoint...');
      const adminResponse = await fetch(
        `${apiBase}/education/cms/sections`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      if (adminResponse.ok) {
        const result = await adminResponse.json();
        if (result.ok && Array.isArray(result.data)) {
          curriculumSection = result.data.find(s => s.section_type === 'education-curriculum-overview-shell');
          console.log('[EducationCurriculumOverviewForm] ✓ Got data from admin endpoint');
        }
      } else if (adminResponse.status === 401) {
        console.log('[EducationCurriculumOverviewForm] Admin endpoint unauthorized, trying public endpoint...');
      }
      
      // Fallback to public endpoint
      if (!curriculumSection) {
        const publicResponse = await fetch(
          `${apiBase}/education/cms/sections?published=true`,
          { 
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          }
        );
        
        if (publicResponse.ok) {
          const result = await publicResponse.json();
          if (result.ok && Array.isArray(result.data)) {
            curriculumSection = result.data.find(s => s.section_type === 'education-curriculum-overview-shell');
            console.log('[EducationCurriculumOverviewForm] ✓ Got data from public endpoint');
          }
        }
      }
      
      if (!curriculumSection) {
        throw new Error('Curriculum overview section not found');
      }

      setSectionId(curriculumSection.id);

      // Check for pending changes
      let pendingFieldsMap = {};
      try {
        const pendingResponse = await fetch(
          `${apiBase}/cms/section/${curriculumSection.id}/with-pending`,
          { credentials: 'include', headers: { 'Content-Type': 'application/json' } }
        );
        
        if (pendingResponse.ok) {
          const pendingResult = await pendingResponse.json();
          if (pendingResult.success && pendingResult.data && pendingResult.data.pending_changes) {
            const { pending_changes } = pendingResult.data;
            if (Array.isArray(pending_changes)) {
              pending_changes.forEach(change => {
                if (change.status === 'pending' && change.locale === 'en') {
                  if (change.field_id && change.field_id.includes('tracks.')) {
                    const match = change.field_id.match(/tracks\.(\d+)\./);
                    if (match) {
                      const trackIdx = parseInt(match[1]);
                      pendingFieldsMap[`track_${trackIdx}`] = true;
                    }
                  }
                }
              });
            }
          }
        }
      } catch (err) {
        console.warn('[EducationCurriculumOverviewForm] Pending changes check failed:', err.message);
      }

      // Extract published content
      let published = curriculumSection.content_published;
      if (typeof published === 'string') {
        try {
          published = JSON.parse(published);
        } catch (e) {
          published = {};
        }
      }

      const publishedEn = published.en || {};
      const newFormData = {
        title: publishedEn.title || '',
        summary: publishedEn.summary || '',
        note: publishedEn.note || '',
        pillars: Array.isArray(publishedEn.pillars) ? publishedEn.pillars : [],
        tracks: Array.isArray(publishedEn.tracks) ? publishedEn.tracks : []
      };

      setFormData(newFormData);
      setOriginalData(newFormData);
      setPendingFields(pendingFieldsMap);
    } catch (err) {
      console.error('[EducationCurriculumOverviewForm] Load error:', err);
      toast.error('Error loading form: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Force re-render of tracks whenever mediaMap updates
    // This ensures track images show up when mediaMap is loaded
  }, [mediaMap]);

  const handleFieldChange = (field, value) => {
    setModalTrackData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmitOverview = async () => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    if (!formData.title || !formData.summary) {
      toast.warning('Please fill in title and summary');
      return;
    }

    setSubmitting(prev => ({ ...prev, 'overview': true }));

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: 'overview.update',
            field_name: 'Update Overview',
            old_value: { title: originalData.title, summary: originalData.summary, note: originalData.note },
            new_value: { title: formData.title, summary: formData.summary, note: formData.note },
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        toast.success('Overview submitted for approval');
        setOriginalData(prev => ({
          ...prev,
          title: formData.title,
          summary: formData.summary,
          note: formData.note
        }));
      } else {
        toast.error(result.error || result.message || 'Submit failed');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, 'overview': false }));
    }
  };

  const handleSubmitPillars = async () => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    if (formData.pillars.length === 0 || formData.pillars.some(p => !p.title || !p.description)) {
      toast.warning('Please fill in all pillar fields');
      return;
    }

    setSubmitting(prev => ({ ...prev, 'pillars': true }));

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: 'pillars.update',
            field_name: 'Update All Pillars',
            old_value: originalData.pillars,
            new_value: formData.pillars,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        toast.success('Pillars submitted for approval');
        setOriginalData(prev => ({
          ...prev,
          pillars: formData.pillars
        }));
      } else {
        toast.error(result.error || result.message || 'Submit failed');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, 'pillars': false }));
    }
  };

  const handleImageUpload = async (trackIndex, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error(`Max 5MB, got ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only JPG, PNG, WebP allowed');
      return;
    }

    setImageUploading(prev => ({ ...prev, [trackIndex]: true }));
    setImageProgress(prev => ({ ...prev, [trackIndex]: 0 }));

    try {
      const formDataToUpload = new FormData();
      formDataToUpload.append('file', file);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100);
          setImageProgress(prev => ({ ...prev, [trackIndex]: percent }));
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const response = JSON.parse(xhr.responseText);
            if (response.success || response.ok) {
              const mediaId = response.media_id || response.id;
              if (mediaId) {
                setModalTrackData(prev => ({...prev, image: mediaId}));
                toast.success('✓ Image uploaded');
              }
            } else {
              toast.error(response.error || 'Upload failed');
            }
          } catch (parseErr) {
            toast.error('Invalid server response');
          }
        } else {
          toast.error('Upload failed');
        }
        setImageUploading(prev => ({ ...prev, [trackIndex]: false }));
      });

      xhr.addEventListener('error', () => {
        toast.error('Upload failed - network error');
        setImageUploading(prev => ({ ...prev, [trackIndex]: false }));
      });

      xhr.open('POST', `${apiBase}/education/cms/media/upload`);
      xhr.withCredentials = true;
      xhr.send(formDataToUpload);
    } catch (err) {
      toast.error('Upload error: ' + err.message);
      setImageUploading(prev => ({ ...prev, [trackIndex]: false }));
    }
  };

  const handleSubmitTrack = async (trackIndex) => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    const track = modalTrackData;
    if (!track || !track.title || !track.description) {
      toast.warning('Please fill in title and description');
      return;
    }

    const trackKey = `track_${trackIndex}`;
    setSubmitting(prev => ({ ...prev, [trackKey]: true }));

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: `tracks.${trackIndex}.update`,
            field_name: `Update Track: ${track.title}`,
            old_value: originalData.tracks[trackIndex] || {},
            new_value: track,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        toast.success(`Track submitted for approval`);
        
        setPendingFields(prev => ({
          ...prev,
          [`track_${trackIndex}`]: true
        }));

        setFormData(prev => ({
          ...prev,
          tracks: prev.tracks.map((t, i) => i === trackIndex ? track : t)
        }));

        setOriginalData(prev => ({
          ...prev,
          tracks: prev.tracks.map((t, i) => i === trackIndex ? track : t)
        }));

        setEditingTrackIndex(null);
        setModalTrackData(null);
      } else {
        if (response.status === 409) {
          toast.error('This track already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [trackKey]: false }));
    }
  };

  if (loading) {
    return (
      <div className="education-levels-form-container">
        <div className="loading">
          <div className="loading-spinner"></div>
          Loading Curriculum Overview...
        </div>
      </div>
    );
  }

  return (
    <div className="education-levels-form-container">
      <ToastContainer />

      <div className="hero-form-wrapper">
        <div className="hero-form-header">
          <h2 className="hero-form-title">Curriculum Overview</h2>
          <p className="hero-form-description">Manage curriculum pathway, pillars, and learning tracks</p>
        </div>

        {/* TAB NAVIGATION */}
        <div className="tabs-wrapper">
          <div className="tabs-nav">
            <button 
              className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`} 
              onClick={() => setActiveTab('overview')}
            >
              <span className="tab-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                </svg>
              </span>
              <span>Overview</span>
            </button>
            <button 
              className={`tab-button ${activeTab === 'pillars' ? 'active' : ''}`} 
              onClick={() => setActiveTab('pillars')}
            >
              <span className="tab-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="7" height="7"></rect>
                  <rect x="14" y="3" width="7" height="7"></rect>
                  <rect x="14" y="14" width="7" height="7"></rect>
                  <rect x="3" y="14" width="7" height="7"></rect>
                </svg>
              </span>
              <span>Pillars</span>
            </button>
            <button 
              className={`tab-button ${activeTab === 'tracks' ? 'active' : ''}`} 
              onClick={() => setActiveTab('tracks')}
            >
              <span className="tab-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 9h12M6 15h12M4 3h16a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z"></path>
                </svg>
              </span>
              <span>Tracks</span>
            </button>
          </div>
        </div>

        {/* TAB CONTENT */}
        <div className="tabs-content">

          {/* OVERVIEW TAB */}
          {activeTab === 'overview' && (
            <div className="tab-pane">
              <div className="form-section">
                <h3 className="form-section-title">Overview Information</h3>
                
                <div className="form-field-card">
                  <label>Title *</label>
                  <input
                    type="text"
                    maxLength="200"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))}
                    placeholder="e.g., National & International Curriculum Pathway"
                    className="text-input"
                  />
                </div>

                <div className="form-field-card">
                  <label>Summary *</label>
                  <textarea
                    maxLength="500"
                    value={formData.summary}
                    onChange={(e) => setFormData(prev => ({...prev, summary: e.target.value}))}
                    placeholder="Brief overview of the curriculum..."
                    className="textarea-input"
                    rows="3"
                  />
                </div>

                <div className="form-field-card">
                  <label>Additional Note</label>
                  <textarea
                    maxLength="500"
                    value={formData.note}
                    onChange={(e) => setFormData(prev => ({...prev, note: e.target.value}))}
                    placeholder="Any additional information..."
                    className="textarea-input"
                    rows="2"
                  />
                </div>

                <button 
                  onClick={handleSubmitOverview} 
                  disabled={submitting['overview'] || !formData.title || !formData.summary} 
                  className="submit-btn"
                >
                  {submitting['overview'] ? 'Submitting...' : (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{display: 'inline', marginRight: '4px', verticalAlign: 'middle'}}>
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"></path>
                      </svg>
                      Submit Overview for Approval
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* PILLARS TAB */}
          {activeTab === 'pillars' && (
            <div className="tab-pane">
              <div className="form-section">
                <h3 className="form-section-title">Curriculum Pillars</h3>
                
                {formData.pillars.length === 0 ? (
                  <div className="items-empty-state">
                    <div className="empty-icon">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <rect x="3" y="3" width="7" height="7"></rect>
                        <rect x="14" y="3" width="7" height="7"></rect>
                        <rect x="14" y="14" width="7" height="7"></rect>
                        <rect x="3" y="14" width="7" height="7"></rect>
                      </svg>
                    </div>
                    <h4>No pillars found</h4>
                    <p>Curriculum pillars will appear here</p>
                  </div>
                ) : (
                  <>
                    {formData.pillars.map((pillar, idx) => (
                      <div key={idx} className="form-field-card">
                        <div className="field-header">
                          <label>Pillar {idx + 1}</label>
                        </div>
                        <input
                          type="text"
                          maxLength="100"
                          value={pillar.title || ''}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            pillars: prev.pillars.map((p, i) => i === idx ? {...p, title: e.target.value} : p)
                          }))}
                          placeholder="Pillar title..."
                          className="text-input"
                        />
                        <textarea
                          maxLength="300"
                          value={pillar.description || ''}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            pillars: prev.pillars.map((p, i) => i === idx ? {...p, description: e.target.value} : p)
                          }))}
                          placeholder="Pillar description..."
                          className="textarea-input"
                          rows="2"
                          style={{marginTop: '8px'}}
                        />
                      </div>
                    ))}

                    <button 
                      onClick={handleSubmitPillars} 
                      disabled={submitting['pillars'] || formData.pillars.some(p => !p.title || !p.description)} 
                      className="submit-btn"
                    >
                      {submitting['pillars'] ? 'Submitting...' : (
                        <>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{display: 'inline', marginRight: '4px', verticalAlign: 'middle'}}>
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"></path>
                          </svg>
                          Submit All Pillars for Approval
                        </>
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* TRACKS TAB */}
          {activeTab === 'tracks' && (
            <div className="tab-pane">
              <div className="form-section">
                <h3 className="form-section-title">Learning Tracks</h3>
                
                {formData.tracks.length === 0 ? (
                  <div className="items-empty-state">
                    <div className="empty-icon">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M6 9h12M6 15h12M4 3h16a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z"></path>
                      </svg>
                    </div>
                    <h4>No tracks found</h4>
                    <p>Learning tracks will appear here</p>
                  </div>
                ) : (
                  <div className="level-cards-grid">
                    {formData.tracks.map((track, idx) => (
                      <button
                        key={idx}
                        type="button"
                        className="level-card"
                        onClick={() => {
                          setEditingTrackIndex(idx);
                          setModalTrackData({...track});
                        }}
                      >
                        {track.image && mediaMap[track.image] && (
                          <div className="level-card-image">
                            <img src={mediaMap[track.image]} alt={track.title} />
                          </div>
                        )}
                        {(!track.image || !mediaMap[track.image]) && (
                          <div className="level-card-image-placeholder">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                              <circle cx="8.5" cy="8.5" r="1.5"></circle>
                              <polyline points="21 15 16 10 5 21"></polyline>
                            </svg>
                          </div>
                        )}
                        
                        <div className="level-card-content">
                          <div className="level-card-header">
                            <h4 className="level-card-title">{track.title}</h4>
                            {pendingFields[`track_${idx}`] && (
                              <span className="level-card-badge">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                  <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.2"/>
                                  <path d="M12 6v6l4 2.4" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
                                </svg>
                              </span>
                            )}
                          </div>
                          {track.subtitle && (
                            <p className="level-card-subtitle">{track.subtitle}</p>
                          )}
                          {track.description && (
                            <p className="level-card-desc">{track.description.substring(0, 60)}...</p>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* EDIT TRACK MODAL */}
      {editingTrackIndex !== null && modalTrackData && (
        <div className="modal-overlay" onClick={() => {
          setEditingTrackIndex(null);
          setModalTrackData(null);
        }}>
          <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Track {editingTrackIndex + 1}: {modalTrackData.title}</h3>
              <button className="modal-close" onClick={() => {
                setEditingTrackIndex(null);
                setModalTrackData(null);
              }}>×</button>
            </div>

            <div className="modal-body">
              {/* IMAGE PREVIEW */}
              <div className="modal-field">
                <label>Track Image</label>
                {modalTrackData.image && mediaMap[modalTrackData.image] && (
                  <div className="modal-image-preview">
                    <img src={mediaMap[modalTrackData.image]} alt="Track preview" />
                  </div>
                )}
                <div className="image-upload-area">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => handleImageUpload(editingTrackIndex, e)}
                    disabled={imageUploading[editingTrackIndex]}
                    className="image-input"
                    id={`modal-track-image-input-${editingTrackIndex}`}
                  />
                  <label htmlFor={`modal-track-image-input-${editingTrackIndex}`} className={`upload-label ${imageUploading[editingTrackIndex] ? 'uploading' : ''}`}>
                    {imageUploading[editingTrackIndex] ? `Uploading ${imageProgress[editingTrackIndex]}%...` : (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{display: 'inline', marginRight: '4px', verticalAlign: 'middle'}}>
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                          <polyline points="17 8 12 3 7 8"></polyline>
                          <line x1="12" y1="3" x2="12" y2="15"></line>
                        </svg>
                        Upload Image
                      </>
                    )}
                  </label>
                </div>
                <span className="hint">Max 5MB • JPG, PNG, WebP</span>
              </div>

              {/* TITLE FIELD */}
              <div className="modal-field">
                <label>Title *</label>
                <input
                  type="text"
                  maxLength="100"
                  value={modalTrackData.title || ''}
                  onChange={(e) => handleFieldChange('title', e.target.value)}
                  placeholder="e.g., Preschool"
                  className="modal-input"
                />
              </div>

              {/* SUBTITLE FIELD */}
              <div className="modal-field">
                <label>Subtitle</label>
                <input
                  type="text"
                  maxLength="100"
                  value={modalTrackData.subtitle || ''}
                  onChange={(e) => handleFieldChange('subtitle', e.target.value)}
                  placeholder="e.g., Ages 3-5"
                  className="modal-input"
                />
              </div>

              {/* DESCRIPTION FIELD */}
              <div className="modal-field">
                <label>Description *</label>
                <textarea
                  maxLength="500"
                  value={modalTrackData.description || ''}
                  onChange={(e) => handleFieldChange('description', e.target.value)}
                  placeholder="Track description..."
                  className="modal-textarea"
                  rows="4"
                />
              </div>

              {/* HIGHLIGHTS FIELD */}
              <div className="modal-field highlights-field">
                <div className="highlights-header">
                  <label>Highlight Points (Max 5)</label>
                  <span className="highlights-count">
                    {(modalTrackData.highlights || []).length} / 5
                  </span>
                </div>

                <div className="highlights-container">
                  {/* HIGHLIGHTS LIST */}
                  {modalTrackData.highlights && modalTrackData.highlights.length > 0 ? (
                    <div className="highlights-list">
                      {modalTrackData.highlights.map((highlight, hIdx) => (
                        <div key={hIdx} className="highlight-item">
                          <div className="highlight-number">{hIdx + 1}.</div>
                          <input
                            type="text"
                            maxLength="150"
                            value={highlight}
                            onChange={(e) => {
                              const newHighlights = [...(modalTrackData.highlights || [])];
                              newHighlights[hIdx] = e.target.value;
                              handleFieldChange('highlights', newHighlights);
                            }}
                            placeholder="Enter highlight text..."
                            className="highlight-input"
                          />
                          <button
                            type="button"
                            className="highlight-delete"
                            onClick={() => {
                              const newHighlights = modalTrackData.highlights.filter((_, i) => i !== hIdx);
                              handleFieldChange('highlights', newHighlights);
                            }}
                            title="Delete"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="18" y1="6" x2="6" y2="18"></line>
                              <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : null}

                  {/* ADD NEW HIGHLIGHT BUTTON */}
                  {(!modalTrackData.highlights || modalTrackData.highlights.length < 5) && (
                    <button
                      type="button"
                      className="highlight-add-btn"
                      onClick={() => {
                        const newHighlights = [...(modalTrackData.highlights || []), ''];
                        handleFieldChange('highlights', newHighlights);
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{marginRight: '6px'}}>
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                      </svg>
                      Add Highlight
                    </button>
                  )}

                  {/* EMPTY STATE */}
                  {(!modalTrackData.highlights || modalTrackData.highlights.length === 0) && (
                    <div className="highlights-empty">
                      <p>No highlights yet. Click "Add Highlight" to create one.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => {
                setEditingTrackIndex(null);
                setModalTrackData(null);
              }}>Cancel</button>
              <button
                className="btn-submit"
                disabled={submitting[`track_${editingTrackIndex}`] || !modalTrackData.title || !modalTrackData.description}
                onClick={() => handleSubmitTrack(editingTrackIndex)}
              >
                {submitting[`track_${editingTrackIndex}`] ? 'Submitting...' : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{display: 'inline', marginRight: '4px', verticalAlign: 'middle'}}>
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"></path>
                    </svg>
                    Save & Submit for Approval
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
